import React, { useState, useMemo } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Box,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Grid,
    IconButton,
    Tooltip,
    Paper,
    Typography,
    ListSubheader,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getIcon, departmentIcons, getCategories, searchIcons } from '../../Setup/Utils/iconHelper';

/**
 * Simple Icon Picker - Dropdown Style (Default)
 */
export const IconPickerDropdown = ({ label, value, onChange, error, helperText, ...props }) => {
    const categories = useMemo(() => getCategories(), []);
    
    const iconsByCategory = useMemo(() => {
        const grouped = {};
        categories.forEach(cat => {
            grouped[cat] = departmentIcons.filter(icon => icon.category === cat);
        });
        return grouped;
    }, [categories]);

    return (
        <FormControl fullWidth error={error} {...props}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value || ''}
                label={label}
                onChange={(e) => onChange(e.target.value)}
            >
                <MenuItem value="">
                    <em>No Icon</em>
                </MenuItem>
                {categories.map((category) => [
                    <ListSubheader key={`header-${category}`}>{category}</ListSubheader>,
                    ...iconsByCategory[category].map((icon) => {
                        const IconComponent = getIcon(icon.name, 'outlined');
                        return (
                            <MenuItem key={icon.name} value={icon.name}>
                                <ListItemIcon>
                                    <IconComponent fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={icon.label} />
                            </MenuItem>
                        );
                    })
                ])}
            </Select>
            {helperText && (
                <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mt: 0.5, ml: 1.5 }}>
                    {helperText}
                </Typography>
            )}
        </FormControl>
    );
};

/**
 * Advanced Icon Picker - Grid Style with Search & Categories
 */
export const IconPickerGrid = ({ label, value, onChange, error, helperText }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Core');

    const categories = useMemo(() => getCategories(), []);

    const filteredIcons = useMemo(() => {
        if (searchQuery) {
            return searchIcons(searchQuery);
        }
        return departmentIcons.filter(icon => icon.category === selectedCategory);
    }, [searchQuery, selectedCategory]);

    const handleIconSelect = (iconName) => {
        onChange(iconName);
    };

    const SelectedIcon = value ? getIcon(value, 'outlined') : null;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {label}
                </Typography>
                
                {/* Selected Icon Preview */}
                {SelectedIcon && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <SelectedIcon color="primary" />
                        <Typography variant="body2">
                            {departmentIcons.find(i => i.name === value)?.label || value}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Search */}
            <TextField
                fullWidth
                size="small"
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            {/* Category Tabs */}
            {!searchQuery && (
                <Tabs
                    value={selectedCategory}
                    onChange={(e, newValue) => setSelectedCategory(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    {categories.map((category) => (
                        <Tab key={category} label={category} value={category} />
                    ))}
                </Tabs>
            )}

            {/* Icon Grid */}
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <Grid container spacing={1}>
                    {filteredIcons.map((icon) => {
                        const IconComponent = getIcon(icon.name, 'outlined');
                        const isSelected = value === icon.name;

                        return (
                            <Grid item key={icon.name}>
                                <Tooltip title={icon.label} arrow>
                                    <IconButton
                                        onClick={() => handleIconSelect(icon.name)}
                                        sx={{
                                            border: 1,
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            bgcolor: isSelected ? 'primary.light' : 'transparent',
                                            '&:hover': {
                                                bgcolor: isSelected ? 'primary.light' : 'action.hover',
                                            },
                                        }}
                                    >
                                        <IconComponent 
                                            fontSize="small" 
                                            color={isSelected ? 'primary' : 'action'}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>

            {error && helperText && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {helperText}
                </Typography>
            )}
        </Paper>
    );
};

/**
 * Default export - Simple dropdown version
 */
const IconPicker = IconPickerDropdown;

export default IconPicker;

